import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { useCallback, useEffect } from "react";
import { UploadDto, bffApi } from "../../../autogen/bff-api";
import { useApiError } from "../../errors/useApiError";
import { getPusher } from "../../pusher";
import { useAppDispatch } from "../../redux/hooks";

export type DocumentUploadContext =
  | "SourcingEventV2"
  | "BidV2"
  | "Contract"
  | "ContractMessageAttachment"
  | "Project"
  | "NegotiationRoundV2"
  | "Need"
  | "BidV2Rejection"
  | "ParticipationRequestRejection"
  | "BidV2QualificationEvidence"
  | "BidV2AwardCriterionAttachment"
  | "BidV2MinimumRequirementAttachment"
  | "SeV2QuestionAttachment"
  | "SeV2AnswerAttachment"
  | "SeV2AnnouncementAttachment";

export const useDocumentsUploader = ({
  entityId,
  eventId,
  entityType,
  refetch,
  qualificationCriterionKey,
  awardCriterionId,
  minimumRequirementId,
  questionId,
  onUploadRejected,
  reportsFailures = true,
}: {
  entityType: DocumentUploadContext;
  entityId: string;
  eventId?: string;
  refetch: () => void;
  qualificationCriterionKey?: string;
  awardCriterionId?: string;
  minimumRequirementId?: string;
  questionId?: string;
  onUploadRejected?: (uploadId: string) => void;
  reportsFailures?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const displayer = useApiError();

  useEffect(() => {
    const pusher = getPusher();
    const channel = pusher.subscribe(entityId);
    const onUploadFailed = (event?: { uploadId?: string }) => {
      if (event?.uploadId) onUploadRejected?.(event.uploadId);
      refetch();
    };
    channel.bind("document-added", refetch);
    channel.bind("upload-failed", onUploadFailed);
    pusher.connection.bind("connected", refetch);

    return () => {
      channel.unbind("document-added", refetch);
      channel.unbind("upload-failed", onUploadFailed);
      pusher.connection.unbind("connected", refetch);
    };
  }, [entityId, refetch, onUploadRejected]);

  const createPostRequest = useCallback(
    async (file: {
      id: string;
      name: string;
      extension: string;
      file: File;
    }): Promise<
      { data: UploadDto } | { error: FetchBaseQueryError | SerializedError }
    > => {
      switch (entityType) {
        case "BidV2QualificationEvidence": {
          if (!qualificationCriterionKey)
            throw new Error("Missing qualification criterion key");
          const response = dispatch(
            bffApi.endpoints.createUploadForBidQualificationEvidence.initiate(
              {
                bidId: entityId,
                uploadId: file.id,
                createBidQualificationEvidenceUploadRequestDto: {
                  fileName: file.name,
                  fileSize: file.file.size,
                  fileExtension: file.extension,
                  mimeType: file.file.type,
                  criterionKey: qualificationCriterionKey,
                },
              },
            ),
          );
          response.reset();
          return await response;
        }
        case "BidV2AwardCriterionAttachment": {
          if (!awardCriterionId) throw new Error("Missing award criterion id");
          const response = dispatch(
            bffApi.endpoints.createUploadForBidAwardCriterionAttachment.initiate(
              {
                bidId: entityId,
                uploadId: file.id,
                createBidAwardCriterionAttachmentUploadRequestDto: {
                  fileName: file.name,
                  fileSize: file.file.size,
                  fileExtension: file.extension,
                  mimeType: file.file.type,
                  criterionId: awardCriterionId,
                },
              },
            ),
          );
          response.reset();
          return await response;
        }
        case "BidV2MinimumRequirementAttachment": {
          if (!minimumRequirementId) throw new Error("Missing minimum requirement id");
          const response = dispatch(
            bffApi.endpoints.createUploadForBidMinimumRequirementAttachment.initiate(
              {
                bidId: entityId,
                uploadId: file.id,
                createBidMinimumRequirementAttachmentUploadRequestDto: {
                  fileName: file.name,
                  fileSize: file.file.size,
                  fileExtension: file.extension,
                  mimeType: file.file.type,
                  minimumRequirementId,
                },
              },
            ),
          );
          response.reset();
          return await response;
        }
        case "BidV2Rejection": {
          if (!eventId) throw new Error("Missing event id");
          const response = dispatch(
            bffApi.endpoints.createUploadForBidRejection.initiate({
              eventId,
              bidId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "ParticipationRequestRejection": {
          if (!eventId) throw new Error("Missing event id");
          const response = dispatch(
            bffApi.endpoints.createUploadForParticipationRequestRejection.initiate(
              {
                eventId,
                requestId: entityId,
                uploadId: file.id,
                createUploadRequestDto: {
                  fileName: file.name,
                  fileSize: file.file.size,
                  fileExtension: file.extension,
                  mimeType: file.file.type,
                },
              },
            ),
          );
          response.reset();
          return await response;
        }
        case "NegotiationRoundV2": {
          if (!eventId) throw new Error("Missing event id");
          const response = dispatch(
            bffApi.endpoints.createUploadForNegotiationRound.initiate({
              eventId,
              roundId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "BidV2": {
          const response = dispatch(
            bffApi.endpoints.createUploadForBid.initiate({
              bidId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "Contract": {
          const response = dispatch(
            bffApi.endpoints.createUploadForContract.initiate({
              contractId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "ContractMessageAttachment": {
          const response = dispatch(
            bffApi.endpoints.createUploadForContractMessageAttachment.initiate({
              contractId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "SeV2QuestionAttachment": {
          const response = dispatch(
            bffApi.endpoints.createUploadForSeQuestionAttachment.initiate({
              eventId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "SeV2AnswerAttachment": {
          if (!questionId) throw new Error("Missing question id");
          const response = dispatch(
            bffApi.endpoints.createUploadForSeAnswerAttachment.initiate({
              eventId: entityId,
              questionId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "SeV2AnnouncementAttachment": {
          const response = dispatch(
            bffApi.endpoints.createUploadForSeAnnouncementAttachment.initiate({
              eventId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "SourcingEventV2": {
          const response = dispatch(
            bffApi.endpoints.createUploadForSourcingEvent.initiate({
              eventId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "Project": {
          const response = dispatch(
            bffApi.endpoints.createUploadForProject.initiate({
              projectId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "Need": {
          const response = dispatch(
            bffApi.endpoints.createUploadForNeed.initiate({
              needId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
      }
    },
    [
      dispatch,
      entityId,
      entityType,
      eventId,
      qualificationCriterionKey,
      awardCriterionId,
      minimumRequirementId,
      questionId,
    ],
  );

  const upload = useCallback(
    async (file: {
      id: string;
      name: string;
      extension: string;
      file: File;
    }): Promise<string | null> => {
      const postRequestResult = await createPostRequest(file);

      if ("data" in postRequestResult) {
        const data = postRequestResult.data;
        const form = new FormData();

        form.append("x-amz-date", data.renamedXAmzDate);
        form.append("x-amz-signature", data.renamedXAmzSignature);
        form.append("x-amz-algorithm", data.renamedXAmzAlgorithm);
        form.append("x-amz-credential", data.renamedXAmzCredential);
        form.append("policy", data.policy);
        form.append("key", data.key);
        form.append("file", file.file);

        // The S3 POST policy is signed for data.url; any other base URL gets a 403.
        const response = await fetch(data.url, {
          method: "POST",
          body: form,
        }).catch(() => null);

        if (!response?.ok) {
          if (reportsFailures) {
            displayer.trigger({
              message: `could not perform upload, response: ${response?.body}`,
            });
          }
          return null;
        }

        return data.key;
      } else {
        if (reportsFailures) displayer.trigger(postRequestResult.error);
        return null;
      }
    },
    [createPostRequest, displayer, reportsFailures],
  );

  return {
    upload,
    createPostRequest,
  };
};
